import DOMPurify from 'dompurify';
import React from 'react';
import { useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { selectHelpStyle } from '../../../../../AdviserArea/AdviserAreaSelector';
import { Box } from '../../../../../CommonComponents/OPL/Box/Box';
import { RawButton } from '../../../../../CommonComponents/OPL/Button/Button.styles';
import { Icon } from '../../../../../CommonComponents/OPL/Icon/Icon';
import { Input } from '../../../../../CommonComponents/OPL/Input/Input';
import { Text } from '../../../../../CommonComponents/OPL/Text/Text';

const StyledButton = styled(RawButton)(({ theme }) => ({
  '&&:hover': {
    backgroundColor: theme.colors.neutral[100],
  },
}));

const getDataFromADV = () => ({
  ...ADV.Help,
  sampleClients: ADV.entities.getSampleClients(),
  demoURI: ADV.settings.endpoints.demo,
});

interface Props {
  onDemo(): void;
  onContact(): void;
}

export const HelpMenu = ({ onDemo, onContact }: Props) => {
  const { register, handleSubmit } = useForm<{ search: string }>({ defaultValues: { search: '' } });

  const helpStyle = useSelector(selectHelpStyle);

  const { title, description, sampleClients, demoURI } = getDataFromADV();

  const handleSearchSubmit = handleSubmit(({ search }) => {
    window.open(
      `${ADV.info.resourceCenterUrl}global-search/${encodeURIComponent(search)}`,
      '_blank',
    );
  });
  return (
    <Box p="16">
      <Text fontWeight={500} fontSize="h6" color="neutral.900" mb="12">
        Help Library
      </Text>
      {title && description && (
        <Box mb="16">
          <Text as="h4" fontSize="h4" mb="8">
            {title}
          </Text>

          <Text
            fontSize="small"
            dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(description),
            }}
          />
        </Box>
      )}

      <Box as="form" onSubmit={handleSearchSubmit}>
        {helpStyle !== 'Voya' && (
          <Input
            {...register('search', { required: true })}
            leftIcon="fa fa-search"
            aria-label="Search the Resource Center"
            placeholder="Search the Resource Center"
          />
        )}
      </Box>

      <Box
        width="100%"
        display="grid"
        alignItems="stretch"
        pt="16"
        css={`
          list-style: none;
          grid-template-columns: repeat(3, 1fr);
          grid-auto-rows: 1fr;
          column-gap: ${({ theme }) => theme.space[4]}px;
          row-gap: ${({ theme }) => theme.space[8]}px;
        `}
      >
        {helpStyle !== 'Voya' && (
          <StyledButton
            onClick={() => window.open(`${ADV.info.resourceCenterUrl}financial-planning`, '_blank')}
            aria-label="Open Help Center in new window"
          >
            <Box
              alignItems="center"
              display="flex"
              flexDirection="column"
              height="100%"
              justifyContent="center"
              py="8"
              css={({ theme }) => ({
                gap: theme.space[4],
              })}
            >
              <Box flex="1" display="flex" alignItems="center" justifyContent="center">
                <Icon
                  className="fa fa-search"
                  color="neutral.400"
                  fontSize="h2"
                  css={({ theme }) => ({ fontWeight: theme.fontWeights[400] })}
                />
              </Box>
              <Box flex="1">
                <Text fontWeight={500} fontSize="small">
                  Help Center
                </Text>
              </Box>
            </Box>
          </StyledButton>
        )}

        <StyledButton onClick={onDemo}>
          <Box
            alignItems="center"
            display="flex"
            flexDirection="column"
            height="100%"
            justifyContent="center"
            py="8"
            css={({ theme }) => ({
              gap: theme.space[4],
            })}
          >
            <Box flex="1" display="flex" alignItems="center" justifyContent="center">
              <Icon
                className="fas fa-play"
                color="neutral.400"
                fontSize="h2"
                css={({ theme }) => ({ fontWeight: theme.fontWeights[400] })}
              />
            </Box>
            <Box flex="1">
              <Text fontWeight={500} fontSize="small">
                Watch Demo
              </Text>
            </Box>
          </Box>
        </StyledButton>

        {helpStyle !== 'Voya' && helpStyle !== 'OneDigital' && (
          <StyledButton onClick={onContact}>
            <Box
              alignItems="center"
              display="flex"
              flexDirection="column"
              height="100%"
              justifyContent="center"
              py="8"
              css={({ theme }) => ({
                gap: theme.space[4],
              })}
            >
              <Box flex="1" display="flex" alignItems="center" justifyContent="center">
                <Icon
                  className="far fa-paper-plane"
                  color="neutral.400"
                  fontSize="h2"
                  css={({ theme }) => ({ fontWeight: theme.fontWeights[400] })}
                />
              </Box>
              <Box flex="1">
                <Text fontWeight={500} fontSize="small">
                  Contact Orion Planning
                </Text>
              </Box>
            </Box>
          </StyledButton>
        )}

        {sampleClients?.map(({ id, firstName, lastName }) => (
          <StyledButton
            key={id}
            onClick={() => window.open(`${demoURI}&masterSampleClientId=${id}`, '_blank')}
            aria-label={`Open sample client ${firstName} ${lastName} in new window`}
          >
            <Box
              alignItems="center"
              display="flex"
              flexDirection="column"
              height="100%"
              justifyContent="center"
              py="8"
              css={({ theme }) => ({
                gap: theme.space[4],
              })}
            >
              <Box flex="1" display="flex" alignItems="center" justifyContent="center">
                <Icon
                  className="fas fa-user-tie"
                  color="neutral.400"
                  fontSize="h2"
                  css={({ theme }) => ({ fontWeight: theme.fontWeights[400] })}
                />
              </Box>
              <Box flex="1">
                <Text fontWeight={500} fontSize="small">
                  {`${firstName} ${lastName}`}
                </Text>
              </Box>
            </Box>
          </StyledButton>
        ))}
      </Box>
    </Box>
  );
};
